/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import type { FeatureCollection, Polygon } from 'geojson';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useAtomCallback } from 'jotai/utils';
import { POSTProperties } from '../Post/types';
import { useCallback } from 'react';

import { fetchFlatGeobuf } from '../services/featuresApi';

export async function asyncGetPostData(): Promise<FeatureCollection<Polygon, POSTProperties>> {
  return fetchFlatGeobuf<Polygon, POSTProperties>('canopy_post');
}

export const postDataChunksAtom = atomFamily((_param: string) =>
  atom<FeatureCollection<Polygon, POSTProperties>>({
    type: 'FeatureCollection',
    features: [],
  })
);

export const postDataChunkIdsAtom = atom<string[]>([]);

export const useSetPostDataChunksUsers = (
  postDataChunks: FeatureCollection<Polygon, POSTProperties>[]
) => {
  return useAtomCallback(
    useCallback(
      async (_get, set) => {
        if (postDataChunks.length > 0) return;
        const data = await asyncGetPostData();
        set(postDataChunksAtom('canopy-0'), data);
        set(postDataChunkIdsAtom, current => Array.from(new Set([...current, 'canopy-0'])));
      },
      [postDataChunks.length]
    )
  );
};

export const canopyChunksSelector = atom(get => {
  const postDataChunkIds = get(postDataChunkIdsAtom);
  const postDataChunks = postDataChunkIds.map(id => get(postDataChunksAtom(id)));
  return postDataChunks ?? [];
});
